import type { Entry, EntryDates, Visibility } from "./api";

/**
 * Les dates d'un point, telles que la frise les montre. Fonctions pures : la date du jour se passe
 * en parametre, jamais lue en douce.
 */

const MONTHS_SHORT = [
  "janv.",
  "févr.",
  "mars",
  "avr.",
  "mai",
  "juin",
  "juil.",
  "août",
  "sept.",
  "oct.",
  "nov.",
  "déc.",
] as const;
const MONTHS_LONG = [
  "janvier",
  "février",
  "mars",
  "avril",
  "mai",
  "juin",
  "juillet",
  "août",
  "septembre",
  "octobre",
  "novembre",
  "décembre",
] as const;

type Ymd = { y: number; m: number; d: number };

function ymd(value: string): Ymd {
  const [y, m, d] = value.split("-").map(Number);
  return { y: y ?? 0, m: m ?? 1, d: d ?? 1 };
}

const lastDay = (y: number, m: number): number => new Date(Date.UTC(y, m, 0)).getUTCDate();
const dayNumber = (d: number): string => (d === 1 ? "1er" : String(d));

/** Aujourd'hui, `AAAA-MM-JJ`, a l'heure locale du navigateur. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${String(now.getFullYear())}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * L'intervalle `from`..`to` en francais : « le 12 mars 2014 », « mars 2014 », « 2014 »,
 * « de sept. 2003 à juin 2004 », « du 21 juin au 22 sept. 2007 ».
 */
export function rangeLabel(from: string, to: string): string {
  const a = ymd(from);
  const b = ymd(to);
  if (from === to) return `le ${dayNumber(a.d)} ${MONTHS_LONG[a.m - 1]} ${String(a.y)}`;
  const wholeMonths = a.d === 1 && b.d === lastDay(b.y, b.m);
  if (wholeMonths) {
    if (a.y === b.y && a.m === 1 && b.m === 12) return String(a.y);
    if (a.y === b.y && a.m === b.m) return `${MONTHS_LONG[a.m - 1]} ${String(a.y)}`;
    if (a.m === 1 && b.m === 12) return `de ${String(a.y)} à ${String(b.y)}`;
    return `de ${MONTHS_SHORT[a.m - 1]} ${String(a.y)} à ${MONTHS_SHORT[b.m - 1]} ${String(b.y)}`;
  }
  const head = `${dayNumber(a.d)} ${MONTHS_SHORT[a.m - 1]}`;
  const tail = `${dayNumber(b.d)} ${MONTHS_SHORT[b.m - 1]} ${String(b.y)}`;
  return a.y === b.y ? `du ${head} au ${tail}` : `du ${head} ${String(a.y)} au ${tail}`;
}

/** Une borne en texte court, pour la date en gras : le texte tape, sinon l'intervalle lu. */
function boundText(raw: string | null, from: string | null, to: string | null): string {
  if (raw !== null && raw.trim() !== "") return raw.trim();
  if (from !== null && to !== null) return rangeLabel(from, to).replace(/^le /, "");
  return "";
}

/** La premiere lettre en capitale : « En 4e », « Été 2007 ». */
const capitalize = (text: string): string =>
  text === "" ? text : `${text.charAt(0).toLocaleUpperCase("fr-FR")}${text.slice(1)}`;

/**
 * La date d'un point telle que la frise l'ecrit en gras : « En 4e », « 2005 → 2009 »,
 * « 2012 → aujourd'hui », « À dater ».
 */
export function whenLabel(entry: EntryDates): string {
  if (entry.undated) return "À dater";
  const start = boundText(entry.startRaw, entry.startFrom, entry.startTo);
  if (entry.kind === "point") return capitalize(start);
  const end = entry.ongoing
    ? "aujourd'hui"
    : boundText(entry.endRaw, entry.endFrom, entry.endTo) || "?";
  return `${capitalize(start)} → ${end}`;
}

/** Ce que l'app a compris, pour la carte : « Compris : de sept. 2003 à juin 2004 ». */
export function understoodLabel(entry: Entry): string {
  if (entry.undated || entry.startFrom === null || entry.startTo === null) {
    return "Pas encore de date.";
  }
  if (entry.kind === "point") return `Compris : ${rangeLabel(entry.startFrom, entry.startTo)}`;
  const start = rangeLabel(entry.startFrom, entry.startTo);
  if (entry.ongoing) return `Compris : depuis ${start.replace(/^(le|de|du) /, "")}, en cours`;
  if (entry.endFrom === null || entry.endTo === null) return `Compris : ${start}`;
  return `Compris : ${start}, jusqu'à ${rangeLabel(entry.endFrom, entry.endTo).replace(/^le /, "")}`;
}

/**
 * Vrai pour une date approximative (boule cerclee en pointille) : une saison, un age, une classe,
 * une etape, ou un « vers ». Une date au jour, au mois ou a l'annee est sure.
 */
export function isFuzzy(entry: Entry): boolean {
  if (entry.undated) return false;
  const raw = (entry.startRaw ?? "").trim().toLowerCase();
  if (raw.startsWith("vers")) return true;
  return !["day", "month", "year", null].includes(entry.startPrecision);
}

export const VISIBILITY_LABELS: Record<Visibility, string> = {
  private: "Moi seul",
  family: "La famille",
  custom: "Des proches choisis",
};

/** Le nom accessible d'une boule : « titre, date ». */
export const accessibleName = (entry: Entry): string => `${entry.title}, ${whenLabel(entry)}`;
